/* eslint-disable @typescript-eslint/no-this-alias */
import SparkMD5 from 'spark-md5';

import { DEBUG_MODE } from '../../spdConfig';

import { ECGCaptureBuffer } from './ecgCaptureBuffer';
import { ECGDeviceSignatureService } from './ecgDeviceSignatureService';
import { ECGHeartRateDetector } from './ecgHeartRateDetector';

// Faza badania
export enum ExamPhase {
  Idle = 0,
  Init = 1,
  Recording = 2,
  Finished = 3,
  Saving = 4,
  Done = 5,
}

// Stan łączności / sygnału w danej fazie
export enum Connectivity {
  Ok = 'OK',
  NoSignal = 'NO_SIGNAL',
  WeakSignal = 'WEAK_SIGNAL',
  Connecting = 'CONNECTING',
}

// Jednowartościowy, "kombinowany" kod statusu (łatwy do mapowania na teksty w UI)
export type StatusCode =
  | 'INIT'
  | 'INIT_NO_SIGNAL'
  | 'INIT_WEAK_SIGNAL'
  | 'INIT_RECONNECTING'
  | 'RECORDING'
  | 'RECORDING_NO_SIGNAL'
  | 'RECORDING_WEAK_SIGNAL'
  | 'RECORDING_RECONNECTING'
  | 'FINISHED'
  | 'SAVING'
  | 'DONE';

// Dwuwymiarowa informacja statusowa (Faza + Łączność) + kod scalony
export interface ExamStatus {
  phase: ExamPhase;
  connectivity: Connectivity; // null dla FINISHED
  code: StatusCode;
}

export interface ProgressState {
  ratio: number; // 0..1
  timeLeft: number; // sekundy
}

// Reguły tworzenia kodu scalonego
// eslint-disable-next-line complexity
export function deriveStatusCode(
  phase: ExamPhase,
  conn: Connectivity,
): StatusCode {
  switch (phase) {
    case ExamPhase.Init:
      if (conn === Connectivity.NoSignal) {
        return 'INIT_NO_SIGNAL';
      }
      if (conn === Connectivity.WeakSignal) {
        return 'INIT_WEAK_SIGNAL';
      }
      if (conn === Connectivity.Connecting) {
        return 'INIT_RECONNECTING';
      }
      return 'INIT';
    case ExamPhase.Recording:
      if (conn === Connectivity.NoSignal) {
        return 'RECORDING_NO_SIGNAL';
      }
      if (conn === Connectivity.WeakSignal) {
        return 'RECORDING_WEAK_SIGNAL';
      }
      if (conn === Connectivity.Connecting) {
        return 'RECORDING_RECONNECTING';
      }
      return 'RECORDING';
    case ExamPhase.Finished:
      return 'FINISHED';
    case ExamPhase.Saving:
      return 'SAVING';
    case ExamPhase.Done:
      return 'DONE';
    default:
      // Idle nie jest częścią listy wymaganych statusów — pojawi się dopiero po starcie Init
      return 'INIT';
  }
}

interface ECGManagerConfig {
  FS: number;
  PX_PER_MM: number;
  SPEED: number;
  TRACE_W: number;
  INVERT: boolean;
  LP40: boolean;
  SMOOTH: boolean;
  CAL10MM_MV: boolean;
  UV_PER_LSB: number;
  BASELINE: number;
  AUTO_BASELINE: boolean;
  AUTO_POL: boolean;
  MWI_MS: number;
  THR_K: number;
  REFRACT_MS: number;
  WHISKERS: boolean;
  BOOT_SEC: number;
  EARLY_SEC: number;
  EARLY_RATIO: number;
}

const SIGN_SVC = '0000ffa1-0000-1000-8000-00805f9b34fb';
const DATA_SVC = '0000ffb1-0000-1000-8000-00805f9b34fb';
const DATA_CHAR = '0000ffb2-0000-1000-8000-00805f9b34fb';

type DecoderKind = 'packed12' | 'u16le10';

/* eslint-disable complexity */
export class ECGManager extends EventTarget {
  public readonly config: ECGManagerConfig;

  private capture = new ECGCaptureBuffer();
  private signer = ECGDeviceSignatureService.getInstance();

  // BLE
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private char: BluetoothRemoteGATTCharacteristic | null = null;
  private reconnectTimer: number | null = null;

  // Streaming/clock
  private lastDataAt = 0;
  private tSec = 0;

  // Display baseline/filter
  private baselineVal: number;

  // HR detector
  private hr: ECGHeartRateDetector;

  // Quality
  private x0Buf: Float32Array;
  private zeroBuf: Uint8Array;
  private writeIdx = 0;

  // Progress
  private elapsedInit = 0;
  private elapsedExam = 0;

  // Draw queue (display samples in px)
  private drawQueue: number[] = [];
  private stableStreamSince: number | null = null;
  private startRecordingFired = false;
  private endRecordingFired = false;
  private lpY = 0;
  private ema = 0;
  protected initTimePeriod = 5;
  protected examTimePeriod = 30;

  private status: ExamStatus = {
    phase: ExamPhase.Idle,
    connectivity: Connectivity.NoSignal,
    code: 'INIT',
  };

  private setPhase(phase: ExamPhase) {
    if (phase !== this.status.phase) {
      const code = deriveStatusCode(phase, this.status.connectivity);
      this.status = {
        phase: phase,
        connectivity: this.status.connectivity,
        code,
      };
      this.dispatchEvent(
        new CustomEvent<ExamStatus>('status', { detail: this.status }),
      );
    }
  }
  private setStatus(connectivity: Connectivity) {
    if (connectivity !== this.status.connectivity) {
      const code = deriveStatusCode(this.status.phase, connectivity);
      this.status = { phase: this.status.phase, connectivity, code };
      this.dispatchEvent(
        new CustomEvent<ExamStatus>('status', { detail: this.status }),
      );
    }
  }

  public onStatus(listener: (s: ExamStatus) => void) {
    this.addEventListener('status', (e: Event) => {
      const ce = e as CustomEvent<ExamStatus>;
      listener(ce.detail);
    });
  }

  public getStatus(): ExamStatus {
    return this.status;
  }

  public getProgress(): ProgressState {
    // ratio i timeLeft pozostaw jak dotychczas liczyłeś (na podstawie elapsedInit / elapsedExam)
    const ratio =
      this.status.phase === ExamPhase.Init
        ? Math.min(1, this.elapsedInit / this.initTimePeriod)
        : this.status.phase === ExamPhase.Recording
        ? Math.min(1, this.elapsedExam / this.examTimePeriod)
        : 1;

    const timeLeft =
      this.status.phase === ExamPhase.Init
        ? Math.max(0, this.initTimePeriod - this.elapsedInit)
        : this.status.phase === ExamPhase.Recording
        ? Math.max(0, this.examTimePeriod - this.elapsedExam)
        : 0;

    return { ratio, timeLeft };
  }

  static instance: ECGManager;
  public static getInstance() {
    if (!ECGManager.instance) {
      ECGManager.instance = new ECGManager();
    }

    return ECGManager.instance;
  }

  constructor() {
    super();
    this.config = Object.assign({
      FS: 320,
      PX_PER_MM: 5.0,
      SPEED: 1.0,
      TRACE_W: 1.4,
      INVERT: true,
      LP40: true,
      SMOOTH: false,
      CAL10MM_MV: true,
      UV_PER_LSB: 1.25,
      BASELINE: 3200,
      AUTO_BASELINE: true,
      AUTO_POL: true,
      MWI_MS: 100,
      THR_K: 0.22,
      REFRACT_MS: 360,
      WHISKERS: false,
      BOOT_SEC: 0.5, // krótki rozruch
      EARLY_SEC: 8.0,
      EARLY_RATIO: 2.2,
    } as ECGManagerConfig);
    this.hr = new ECGHeartRateDetector({
      fs: this.config.FS,
      mwiMs: this.config.MWI_MS,
      thrK: this.config.THR_K,
      refractMs: this.config.REFRACT_MS,
      bootSec: this.config.BOOT_SEC,
      earlySec: this.config.EARLY_SEC,
      earlyRatio: this.config.EARLY_RATIO,
      hpHz: 5,
      lpHz: 18,
    });

    // jeśli chcesz reagować w UI wprost po detekcji:
    this.hr.addEventListener('beat', () => {
      // np. lekki „pulse” w UI można też oprzeć na this.hr.beatJustNow()
    });

    this.baselineVal = this.config.BASELINE;
    this.x0Buf = new Float32Array(this.config.FS * 4);
    this.zeroBuf = new Uint8Array(this.config.FS * 4);
  }

  public restart() {
    this.segDefs = [];
    this.builtSegments = [];
    this.capture.start();
    this.startExamSegmentProgress();
  }

  // === Public API ===
  public async connect(): Promise<boolean> {
    try {
      let dev;
      try {
        dev = await navigator.bluetooth.requestDevice({
          filters: [
            { services: [DATA_SVC] },
            { namePrefix: 'WeCardio' },
            { namePrefix: 'UN' },
          ],
          optionalServices: [DATA_SVC, SIGN_SVC],
        });
      } catch {
        dev = await navigator.bluetooth.requestDevice({
          acceptAllDevices: true,
          optionalServices: [DATA_SVC, SIGN_SVC],
        });
      }
      if (!dev) {
        return false;
      }

      this.device = dev;
      if (this.device) {
        this.device.addEventListener(
          'gattserverdisconnected',
          this.onDisconnected,
        );
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.server = await this.device.gatt!.connect();
        const service = await this.server.getPrimaryService(DATA_SVC);
        this.char = await service.getCharacteristic(DATA_CHAR);

        await this.attachNotifications(this.char);

        await this.signer.bind(this.server);
        this.startExamSegmentProgress();
        this.hr.reset();
        return true;
      }
      return false;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      DEBUG_MODE &&
        console.log(
          'ECG connect',
          err && err.message ? err.message : String(err),
        );

      await this.disconnect();
      return false;
    }
  }

  public async disconnect(): Promise<void> {
    try {
      if (this.char) {
        try {
          await this.char.stopNotifications();
        } catch {
          DEBUG_MODE && console.log('ECG disconnect stopNotifications error');
        }
        this.char.removeEventListener(
          'characteristicvaluechanged',
          this.onCharChanged,
        );
      }
      if (this.device && this.device.gatt && this.device.gatt.connected) {
        this.device.gatt.disconnect();
      }
    } finally {
      if (this.reconnectTimer !== null) {
        window.clearTimeout(this.reconnectTimer);
      }
      this.char = null;
      this.server = null;
      this.device = null;
      this.hr.reset();
      this.stableStreamSince = null; // NEW
      this.startRecordingFired = false; // NEW
      this.endRecordingFired = false;
    }
  }

  /** Wywoływać cyklicznie i po napłynięciu danych.
   *  Emituje 'startrecording' gdy strumień jest ciągły ≥ STABLE_MS. */
  private updateStreamingStability(now: number = performance.now()) {
    const STABLE_MS = 5000; // Próg stabilizacji sygnału
    const streaming = this.isStreaming();

    if (!streaming) {
      this.stableStreamSince = null;
      // jeśli chcesz, by startrecording mógł odpalać po każdej przerwie:
      // this.startRecordingFired = false;
      this.setStatus(Connectivity.NoSignal);
      return;
    }

    /*if (this.dropContinuous) {
      this.setStatus(Connectivity.WeakSignal); /// ciągły sygnał oznacza słaby sygnał;
      return;
    }*/

    if (this.stableStreamSince === null) {
      this.stableStreamSince = now;
    }

    if (
      !this.startRecordingFired &&
      now - this.stableStreamSince >= STABLE_MS
    ) {
      this.startRecordingFired = true;
      this.dispatchEvent(new Event('startrecording'));
      this.setStatus(Connectivity.Ok);
    }
  }

  private savedBlob: Blob | null = null;
  private buildSegmentFromCapture(
    fromByte: number,
    skipSamples: number,
    takeSamples: number,
  ): Uint8Array {
    const pkts = this.capture.getParts20B({
      fromByte,
      filterStatus: this.DECODER_KIND === 'packed12', // u16le10: brak nagłówków/statusów
    });

    const out = new Uint8Array(takeSamples * 2);
    const dvOut = new DataView(out.buffer);
    let skipped = 0,
      written = 0;

    const writeSample = (val: number) => {
      if (skipped < skipSamples) {
        skipped++;
        return;
      }
      if (written >= takeSamples) {
        return;
      }
      dvOut.setUint16(written * 2, val & 0xffff, true);
      written++;
    };

    for (const pkt of pkts) {
      if (written >= takeSamples) {
        break;
      }

      if (this.DECODER_KIND === 'u16le10') {
        // 10×Uint16LE bez dekodowania
        const dv = new DataView(pkt.buffer, pkt.byteOffset, 20);
        for (let i = 0; i < 10 && written < takeSamples; i++) {
          const v = dv.getUint16(i * 2, true);
          writeSample(v);
        }
      } else {
        // packed12 — użyj Twojego dekodera
        const arr = this.decodePacked12(
          new DataView(pkt.buffer, pkt.byteOffset, 20),
        );
        if (!arr || arr.length === 0) {
          continue;
        }
        for (let i = 0; i < arr.length && written < takeSamples; i++) {
          writeSample(arr[i]);
        }
      }
    }

    if (written !== takeSamples) {
      throw new Error(
        `Brak danych: zapisano ${written}/${takeSamples} próbek (skip=${skipSamples}). Poczekaj dłużej albo nie wołaj suspendRecording zbyt wcześnie.`,
      );
    }
    return out;
  }

  /** Zwróć pojedynczy segment (jeśli zrobiono jedną sesję) lub sklejone segmenty (jeśli kilka). */
  protected buildSavedBlob(): Blob {
    if (this.segDefs.length === 0) {
      throw new Error('Brak zakończonych sesji (endrecording).');
    }

    // zbuduj brakujące segmenty do cache
    while (this.builtSegments.length < this.segDefs.length) {
      const def = this.segDefs[this.builtSegments.length];
      const seg = this.buildSegmentFromCapture(
        def.fromByte,
        def.skipSamples,
        def.takeSamples,
      );
      this.builtSegments.push(seg);
    }

    // sklej wszystko
    const total = this.builtSegments.reduce((n, b) => n + b.length, 0);
    const flat = new Uint8Array(total);
    let o = 0;
    for (const b of this.builtSegments) {
      flat.set(b, o);
      o += b.length;
    }
    return new Blob([flat], { type: 'application/octet-stream' });
  }

  public async getSavedBlob() {
    //// do sprawdzenia czy wyniki będą wciąż różne
    this.setPhase(ExamPhase.Saving);

    // B) zbuduj PRAWIDŁOWY plik do uploadu
    this.savedBlob = this.buildSavedBlob();
    const buf = new Uint8Array(await this.savedBlob.arrayBuffer());
    const md5hex = await md5Hex(buf);
    const mid16 = md5hex.substring(8, 24); // usuń pierwsze 8 i ostatnie 8
    const key = await this.getKey(mid16, { uppercase: false });
    this.setPhase(ExamPhase.Done);
    return { blob: this.savedBlob, key: key };
  }

  public async getKey(
    mid16: string,
    opts?: { uppercase?: boolean; timeoutMs?: number },
  ) {
    return await this.signer.getKeyFromMid16(mid16, opts);
  }

  public isStreaming(): boolean {
    const isStreaming =
      (this.char || false) &&
      this.isConnected() &&
      performance.now() - this.lastDataAt < 1200;
    return isStreaming;
  }

  public drainDrawQueue(max = 4096): number[] {
    if (!this.drawQueue.length) {
      return [];
    }
    const N = Math.min(max, this.drawQueue.length);
    const out = this.drawQueue.splice(0, N);
    return out;
  }

  public getConfig() {
    return this.config;
  }

  public startExamSegmentProgress(): void {
    this.hr.reset();
    this.setStatus(Connectivity.NoSignal);
    this.setPhase(ExamPhase.Init);
    this.elapsedInit = 0;
    this.elapsedExam = 0;
    this.stableStreamSince = null;
    this.startRecordingFired = false;
    this.endRecordingFired = false;
  }

  public stepProgress(): ProgressState {
    // ile próbek zebrano od startu sesji (Init + część Exam)
    const s = this.totalSamplesSinceStart;

    let ratio = 0,
      timeLeft = 0;

    if (this.status.phase === ExamPhase.Init) {
      const done = Math.min(s, this.initTargetSamples);
      ratio = done / this.initTargetSamples;
      timeLeft = (this.initTargetSamples - done) / this.config.FS;

      // BEZPIECZNIK: jeśli z jakiegoś powodu faza się jeszcze nie przełączyła w onBlePacketFromEngine
      if (done === this.initTargetSamples) {
        this.setPhase(ExamPhase.Recording);
      }
    } else if (this.status.phase === ExamPhase.Recording) {
      const inExam = Math.max(0, s - this.initTargetSamples);
      const done = Math.min(inExam, this.examTargetSamples);
      ratio = done / this.examTargetSamples;
      timeLeft = (this.examTargetSamples - done) / this.config.FS;

      if (done === this.examTargetSamples && !this.endRecordingFired) {
        // Bezpiecznik: zamknij, jeśli nie zrobił tego onBlePacketFromEngine (np. brak DV dalej)
        this.accepting = false;
        this.sessionActive = false;
        this.segDefs.push({
          fromByte: this.markStartByte,
          skipSamples: this.initTargetSamples,
          takeSamples: this.examTargetSamples,
        });
        this.endRecordingFired = true;
        this.dispatchEvent(new Event('endrecording'));
        this.setPhase(ExamPhase.Finished);
      }
    } else if (this.status.phase === ExamPhase.Finished) {
      ratio = 1;
      timeLeft = 0;
    }

    return { ratio, timeLeft };
  }

  // === Internals ===
  private processSampleUnsigned(rawU: number) {
    const Fs = this.config.FS;
    // baseline jednorazowo na starcie
    if (this.tSec === 0) {
      this.baselineVal = this.config.AUTO_BASELINE
        ? rawU
        : this.config.BASELINE;
    }

    const x0 = rawU - this.baselineVal;

    // opcjonalna wolna adaptacja baseline wyłącznie do WYŚWIETLANIA (nie do detekcji)
    if (this.config.AUTO_BASELINE) {
      // bardzo wolny drift, żeby nie „zjadać” QRS
      this.baselineVal += 0.001 * (rawU - this.baselineVal);
    }

    // znacznik strumienia
    this.lastDataAt = performance.now();

    // Bufory jakości
    this.x0Buf[this.writeIdx] = x0;
    this.zeroBuf[this.writeIdx] = rawU === 0 ? 1 : 0;
    this.writeIdx = (this.writeIdx + 1) % this.x0Buf.length;

    // Filtry dla WIDOKU (jak wcześniej)
    let v = x0;
    if (this.config.LP40) {
      this.lpY = this.lpY ?? 0;
      const LP_A = (2 * Math.PI * 40) / (2 * Math.PI * 40 + Fs);
      this.lpY = this.lpY + LP_A * (v - this.lpY);
      v = this.lpY;
    }
    if (this.config.SMOOTH) {
      this.ema = this.ema ?? 0;
      const a = 0.12;
      this.ema += a * (v - this.ema);
      v = this.ema;
    }
    if (this.config.INVERT) {
      v = -v;
    }

    const disp = this.config.CAL10MM_MV ? this.toPx_mV(v) : v;
    this.drawQueue.push(disp);

    // Detekcja na x0 (nie na wygładzonym v)
    this.hr.stepSample(x0, this.tSec);

    // ——— i dopiero TERAZ przesuwamy zegar próbki
    this.tSec += 1 / Fs;
  }

  // 4) Zgodność zegarów w getBpm – przyjmij opcjonalny nowMs i przelicz, jeśli to Date.now()
  public getBpm(): number | null {
    return this.hr.getBpm();
  }

  // 5) Szybki sygnał dla UI: czy beat był "przed chwilą"?
  public beatJustNow(nowMs?: number, windowMs = 140): boolean {
    return this.hr.beatJustNow(windowMs);
  }

  // eslint-disable-next-line complexity
  private toPx_mV(vLSB: number) {
    const uV = this.config.UV_PER_LSB;
    const mV = (vLSB * uV) / 1000.0;
    return mV * (this.config.PX_PER_MM * 10);
  }

  // --- DETEKCJA SYGNAŁU CIĄGŁEGO (anti-artifacts) ---
  // --- Continuous-signal gate (po zdekodowanych próbkach) ---
  private dropContinuous = false;
  private flatChunks = 0;
  private livelyChunks = 0;

  // 12-bit fullscale w Twoich pakietach:
  private readonly FULLSCALE_12 = 4095;

  // progi (zacznij od tych)
  private readonly FLAT_RANGE_LSB = 1; // zakres max-min uznany za „płasko”
  private readonly SAD_PER_SAMPLE_FLAT = 1.5; // średnia |Δ| na próbkę po obcięciu (LSB)
  private readonly ENTER_FLAT_AFTER = 10; // ile płaskich paczek z rzędu, żeby wejść w drop
  private readonly EXIT_FLAT_AFTER = 2; // ile żywych paczek, żeby wyjść z drop

  private samplesActivity(samples: number[]) {
    let min = Infinity,
      max = -Infinity;
    const diffs: number[] = [];
    for (let i = 0; i < samples.length; i++) {
      const v = samples[i];
      if (v < min) {
        min = v;
      }
      if (v > max) {
        max = v;
      }
      if (i > 0) {
        diffs.push(Math.abs(v - samples[i - 1]));
      }
    }
    // obetnij 20% największych różnic (usuwa efekt pojedynczego skoku)
    diffs.sort((a, b) => a - b);
    const keep = Math.max(0, Math.floor(diffs.length * 0.8));
    const sad = diffs.slice(0, keep).reduce((a, b) => a + b, 0);
    const avgSad = keep ? sad / keep : 0;

    const range = max - min;
    const nearTop = min >= this.FULLSCALE_12 - 3;
    const nearBottom = max <= 3;

    const isFlat =
      range <= this.FLAT_RANGE_LSB ||
      avgSad <= this.SAD_PER_SAMPLE_FLAT ||
      nearTop ||
      nearBottom;

    return { isFlat, range, avgSad };
  }

  public onCharChanged = (ev: Event) => {
    const ch = ev.target as BluetoothRemoteGATTCharacteristic | null;
    const dv = ch?.value;
    if (!dv) {
      return;
    }

    // 1) oceń aktywność po ZDEKODOWANYCH próbkach (12 szt.)
    const samples = this.decodePacked12(dv);
    if (samples) {
      const { isFlat } = this.samplesActivity(samples);

      if (isFlat) {
        this.flatChunks++;
        this.livelyChunks = 0;
        if (!this.dropContinuous && this.flatChunks >= this.ENTER_FLAT_AFTER) {
          this.dropContinuous = true;
          this.setStatus(Connectivity.WeakSignal);
        }
      } else {
        this.livelyChunks++;
        this.flatChunks = 0;
        if (this.dropContinuous && this.livelyChunks >= this.EXIT_FLAT_AFTER) {
          this.dropContinuous = false;
          if (this.startRecordingFired) {
            this.setStatus(Connectivity.Ok);
          }
        }
      }
    }

    // 2) nadal parsujemy do rysowania/UI - tylko w odpowiednich fazach badania
    if (
      this.status.phase === ExamPhase.Init ||
      this.status.phase === ExamPhase.Recording ||
      this.status.phase === ExamPhase.Finished
    ) {
      this.lastDataAt = performance.now();
      this.updateStreamingStability(); /// aktualizujemy informację o stanie sygnału

      if (this.accepting) {
        // aktualizujemy dane do wykresu tylko gdy uruchomiliśmy recording
        this.parsePacket(dv);

        // 3) ale do bufora analizy nie zapisujemy, gdy wykryto sygnał ciągły
        if (this.status.connectivity === Connectivity.Ok) {
          this.capture.pushDataView(dv); // bufor 20B
          this.onBlePacketFromEngine(dv); // >> DODAJ: licznik próbek / przełączanie faz
        }
      }
    }
  };

  private async attachNotifications(
    characteristic: BluetoothRemoteGATTCharacteristic,
  ) {
    this.setStatus(Connectivity.Connecting); // code: 'INIT'
    try {
      characteristic.removeEventListener(
        'characteristicvaluechanged',
        this.onCharChanged,
      );
      // eslint-disable-next-line no-empty
    } catch {}
    try {
      await characteristic.startNotifications();
      // eslint-disable-next-line no-empty
    } catch {}
    characteristic.addEventListener(
      'characteristicvaluechanged',
      this.onCharChanged,
    );
  }
  private isConnected(): boolean {
    return !!(this.device && this.device.gatt && this.device.gatt.connected);
  }

  // uniwersalna asercja
  private assertDefined<T>(v: T, msg: string): asserts v is NonNullable<T> {
    if (v === null) {
      throw new Error(msg);
    }
  }

  private async connectOnce() {
    try {
      // 1) urządzenie
      this.assertDefined(this.device, 'Brak wybranego urządzenia');
      const dev = this.device;

      // 2) połączenie GATT
      if (!this.isConnected()) {
        // w niektórych lib.dom `gatt` może być opcjonalne:
        this.assertDefined(dev.gatt, 'Urządzenie nie udostępnia GATT');
        this.server = await dev.gatt.connect();
      }
      this.assertDefined(this.server, 'Połączenie GATT nie powiodło się');
      const server = this.server;

      // 3) usługa + charakterystyka
      const service = await server.getPrimaryService(DATA_SVC);
      const char = await service.getCharacteristic(DATA_CHAR);
      this.char = char;
      await this.attachNotifications(char);
    } catch (e) {
      DEBUG_MODE && console.log('connectOnce faild', e);
    }
  }

  private onDisconnected = async () => {
    DEBUG_MODE && console.log('ECG onDisconnected');
    this.dispatchEvent(new Event('disconnected'));
    this.stableStreamSince = null; // NEW
    this.startRecordingFired = false; // NEW
  };

  // Wariant próbkowania (jak liczyć próbki w 20B)
  private DECODER_KIND: DecoderKind = 'packed12'; // jeśli masz 10×16LE → 'u16le10'

  // Przełączanie faz po liczbie próbek
  private initTargetSamples = 0; // Fs * initTimePeriod
  private examTargetSamples = 0; // Fs * examTimePeriod
  private totalSamplesSinceStart = 0; // licznik wszystkich próbek od startrecording

  // Gating wejścia (przyjmowanie DV) i akumulacja segmentów
  private accepting = false; // blokowane przez suspendRecording()
  private sessionActive = false; // między startrecording a endrecording
  private markStartByte = 0; // bajtowy znacznik w capture na startrecording
  private segDefs: Array<{
    fromByte: number;
    skipSamples: number;
    takeSamples: number;
  }> = [];
  private builtSegments: Uint8Array[] = []; // cache zdekodowanych segmentów (saved format)

  // Szybkie liczenie próbek bez pełnego dekodowania
  private countSamplesInPacket(dv: DataView): number {
    if (dv.byteLength !== 20) {
      return 0;
    }
    if (this.DECODER_KIND === 'u16le10') {
      return 10;
    }
    // packed12: jeśli status (h0&0x80) → 0, w przeciwnym razie 12
    const h0 = dv.getUint8(0);
    return h0 & 0x80 ? 0 : 12;
  }

  /** Ustaw czasy (s), Fs i wyczyść stan poprzednich sesji. */
  public init(params: {
    initSec: number;
    examSec: number;
    decoder?: DecoderKind;
  }) {
    this.initTimePeriod = params.initSec;
    this.examTimePeriod = params.examSec;

    if (params.decoder) {
      this.DECODER_KIND = params.decoder;
    }

    this.initTargetSamples = Math.floor(this.config.FS * this.initTimePeriod);
    this.examTargetSamples = Math.floor(this.config.FS * this.examTimePeriod);

    // wyczyść stan poprzednich sesji (ale NIE czyść capture — tam już są bajty)
    this.totalSamplesSinceStart = 0;
    this.sessionActive = false;
    this.accepting = true;
  }

  /** WYWOŁAJ z listenera 'startrecording' silnika. */
  public startRecording() {
    // oznacz stan od zera
    this.totalSamplesSinceStart = 0;
    this.sessionActive = true;
    this.accepting = true;

    // znacznik bajtowy na **początek całej sesji** (Init + Exam)
    this.markStartByte = this.capture.createMark();
    // (UWAGA: capture.pushDataView(dv) musi się dziać PRZED wywołaniem onBlePacketFromEngine – jak niżej)
  }

  public suspendRecording() {
    this.setPhase(ExamPhase.Done);
    this.accepting = false;
  }

  /** Licz i przełączaj fazy wyłącznie po liczbie próbek. */
  private onBlePacketFromEngine(dv: DataView) {
    if (!this.sessionActive) {
      return;
    }

    const add = this.countSamplesInPacket(dv);
    if (add <= 0) {
      return;
    }

    const before = this.totalSamplesSinceStart;
    const after = before + add;
    this.totalSamplesSinceStart = after;

    // 1) Init → Recording dokładnie po initTargetSamples
    if (
      this.status.phase === ExamPhase.Init &&
      before < this.initTargetSamples &&
      after >= this.initTargetSamples
    ) {
      this.setPhase(ExamPhase.Recording);

      // w definicji segmentu do analizy pomijamy dokładnie initTargetSamples,
      // a bierzemy dokładnie examTargetSamples
      // (wytnę to później z capture w getSavedBlob())
    }

    // 2) Recording → Finished dokładnie po init+exam próbkach
    const needTotal = this.initTargetSamples + this.examTargetSamples;
    if (
      this.status.phase === ExamPhase.Recording &&
      before < needTotal &&
      after >= needTotal
    ) {
      // zatrzymaj przyjmowanie DV (do czasu ewentualnego resume)
      this.accepting = false;
      this.sessionActive = false;

      // zarejestruj definicję segmentu do przycięcia z capture:
      // fromByte = markStartByte (początek całej sesji),
      // skip = initTargetSamples,
      // take = examTargetSamples
      this.segDefs.push({
        fromByte: this.markStartByte,
        skipSamples: this.initTargetSamples,
        takeSamples: this.examTargetSamples,
      });

      // emituj endrecording (jak w Twoim flow)
      if (!this.endRecordingFired) {
        this.endRecordingFired = true;
        this.dispatchEvent(new Event('endrecording'));
      }
      this.setPhase(ExamPhase.Finished);
    }
  }

  /////////////////////////////////////////

  // Oryginalne dekodowanie BLE (12 próbek / 20B) z worda Borsam
  private decodePacked12(dv: DataView): number[] | null {
    const h0 = dv.getUint8(0);
    if (h0 & 0x80) {
      return null;
    } // pakiet statusowy → pomiń
    const h1 = dv.getUint8(1);
    const high = ((h0 & 0x0f) << 8) | h1;

    const out: number[] = [];
    let k = 0,
      i = 2;
    for (; i < 20; i += 3, k++) {
      const ch1_low = dv.getUint8(i);
      const chg = dv.getUint8(i + 1);
      const ch2_low = dv.getUint8(i + 2);
      const hi1_4 = (chg >> 4) & 0x0f;
      const hi2_4 = chg & 0x0f;
      const idx1 = 11 - 2 * k,
        idx2 = idx1 - 1;
      const msb1 = (high >> idx1) & 1,
        msb2 = (high >> idx2) & 1;
      const s1 = (msb1 << 12) | (hi1_4 << 8) | ch1_low; // 13-bit, 0..8191
      const s2 = (msb2 << 12) | (hi2_4 << 8) | ch2_low;
      out.push(s1, s2);
    }
    return out;
  }

  private parsePacket(buf: DataView | ArrayBuffer) {
    const dv = buf instanceof DataView ? buf : new DataView(buf as ArrayBuffer);
    if (!dv || dv.byteLength !== 20) {
      return;
    }
    const arr = this.decodePacked12(dv);
    if (!arr) {
      return;
    }

    for (let i = 0; i < arr.length; i++) {
      this.processSampleUnsigned(arr[i]);
    }
  }

  // Housekeeping timers (quality refresh + stream watchdog restart)
  private reconnectInFlight: Promise<void> | null = null; // single-flight na connectOnce
  private housekeepingRunning = false; // anty-reentrancy ticka
  private housekeepingTimer?: number; // id interwału

  public startHousekeeping() {
    const connectOnceSafely = async () => {
      // single-flight: jeśli już trwa łączenie, tylko je dokończ
      if (!this.reconnectInFlight) {
        this.reconnectInFlight = this.connectOnce()
          .catch()
          .finally(() => {
            this.reconnectInFlight = null;
          });
      }
      // ważne: czekamy na to samo połączenie
      await this.reconnectInFlight;
    };

    const tick = async () => {
      // anty-reentrancy całego ticka – nie uruchamiaj kolejnego, jeśli poprzedni wciąż trwa
      if (this.housekeepingRunning) {
        return;
      }
      this.housekeepingRunning = true;

      try {
        this.updateStreamingStability();

        // snapshot – od tej pory pracujemy na pewnym typie
        const char = this.char;

        if (!char || !this.isConnected()) {
          await connectOnceSafely();
          return;
        }

        const idle = performance.now() - this.lastDataAt;
        if (idle > 2000) {
          try {
            await char.stopNotifications();
          } catch {
            DEBUG_MODE &&
              console.log('startHousekeeping stopNotifications error');
          }

          // (opcjonalnie) jeśli w międzyczasie ktoś podmienił this.char – odpuść:
          if (char !== this.char) {
            return;
          }

          try {
            await this.attachNotifications(char);
            // opcjonalnie: this.setStatus(Connectivity.Connecting);
          } catch {
            await connectOnceSafely();
          }
        }
      } finally {
        this.housekeepingRunning = false;
      }
    };

    // zapamiętaj timer, by móc go wyczyścić np. w stopHousekeeping()
    this.housekeepingTimer = window.setInterval(() => {
      void tick();
    }, 500);
  }

  // (opcjonalnie)
  public stopHousekeeping() {
    if (this.housekeepingTimer) {
      window.clearInterval(this.housekeepingTimer);
      this.housekeepingTimer = undefined;
    }
  }
}

// ------------------------------------------------------------
//  Utils (crypto + base64, bez zewnętrznych bibliotek)
// ------------------------------------------------------------
async function md5Hex(bytes: Uint8Array): Promise<string> {
  // WebCrypto (natywne MD5 brak) → fallback na js-implementację?
  // Dla prostoty użyjemy małego, wbudowanego MD5 – tu minimalna implementacja
  const spark = new SparkMD5.ArrayBuffer();
  spark.append(bytes.buffer as ArrayBuffer);
  return spark.end(); // 32-znakowy hex lowercase
}
